import { useState } from "react";
import PropTypes from "prop-types";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import SabbathSettings from "components/SabbathSettings";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired
};

TabPanel.defaultProps = {
  children: null
};

function ReportsAndSettings() {
  const [currentTab, setCurrentTab] = useState(0);
  
  // Report settings state
  const [reportSettings, setReportSettings] = useState({
    monthlyReports: true,
    weeklyDigest: true,
    dailySummary: false,
    reportFormat: "pdf",
    reportFrequency: "monthly",
    reportRecipients: "self",
    includeFinancials: true,
    includeMaintenace: true,
    includeOccupancy: true,
    includeCompliance: false,
    autoGenerateReports: true,
    reportDeliveryTime: "08:00",
    reportTimezone: "America/New_York"
  });

  const [savedAlert, setSavedAlert] = useState(false);

  const handleReportSettingChange = (field, value) => {
    setReportSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleSaveReportSettings = () => {
    // Here you would save to backend
    setSavedAlert(true);
    setTimeout(() => setSavedAlert(false), 3000);
  };

  const handleSabbathUpdate = (settings) => {
    console.log('Sabbath settings updated:', settings);
    // Handle the update - this would typically call an API
  };

  return (
    <ArgonBox>
      <Card>
        <ArgonBox p={3}>
          <ArgonTypography variant="h5" fontWeight="medium" mb={1}>
            Reports and Settings
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text">
            Configure your reporting preferences and special observance settings
          </ArgonTypography>
        </ArgonBox>

        <ArgonBox px={3}>
          <Tabs value={currentTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Report Settings" icon={<Icon fontSize="small">assessment</Icon>} iconPosition="start" />
            <Tab label="Sabbath Settings" icon={<Icon fontSize="small">schedule</Icon>} iconPosition="start" />
          </Tabs>
        </ArgonBox>

        <ArgonBox p={3}>
          <TabPanel value={currentTab} index={0}>
            {/* Report Settings Tab */}
            <Grid container spacing={3}>
              {/* Report Frequency Section */}
              <Grid item xs={12}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Report Frequency
                </ArgonTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={reportSettings.monthlyReports}
                          onChange={(e) => handleReportSettingChange('monthlyReports', e.target.checked)}
                        />
                      }
                      label="Monthly Reports"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={reportSettings.weeklyDigest}
                          onChange={(e) => handleReportSettingChange('weeklyDigest', e.target.checked)}
                        />
                      }
                      label="Weekly Digest"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={reportSettings.dailySummary}
                          onChange={(e) => handleReportSettingChange('dailySummary', e.target.checked)}
                        />
                      }
                      label="Daily Summary"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={reportSettings.autoGenerateReports}
                          onChange={(e) => handleReportSettingChange('autoGenerateReports', e.target.checked)}
                        />
                      }
                      label="Auto-generate Reports"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Report Configuration */}
              <Grid item xs={12}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Report Configuration
                </ArgonTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Report Format</InputLabel>
                      <Select
                        value={reportSettings.reportFormat}
                        label="Report Format"
                        onChange={(e) => handleReportSettingChange('reportFormat', e.target.value)}
                      >
                        <MenuItem value="pdf">PDF</MenuItem>
                        <MenuItem value="excel">Excel</MenuItem>
                        <MenuItem value="csv">CSV</MenuItem>
                        <MenuItem value="html">HTML Email</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Delivery Time</InputLabel>
                      <Select
                        value={reportSettings.reportDeliveryTime}
                        label="Delivery Time"
                        onChange={(e) => handleReportSettingChange('reportDeliveryTime', e.target.value)}
                      >
                        <MenuItem value="06:00">6:00 AM</MenuItem>
                        <MenuItem value="08:00">8:00 AM</MenuItem>
                        <MenuItem value="12:00">12:00 PM</MenuItem>
                        <MenuItem value="17:00">5:00 PM</MenuItem>
                        <MenuItem value="20:00">8:00 PM</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Recipients</InputLabel>
                      <Select
                        value={reportSettings.reportRecipients}
                        label="Recipients"
                        onChange={(e) => handleReportSettingChange('reportRecipients', e.target.value)}
                      >
                        <MenuItem value="self">Only Me</MenuItem>
                        <MenuItem value="managers">Property Managers</MenuItem>
                        <MenuItem value="executives">Executives</MenuItem>
                        <MenuItem value="custom">Custom List</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Report Content */}
              <Grid item xs={12}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Report Content
                </ArgonTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={reportSettings.includeFinancials}
                          onChange={(e) => handleReportSettingChange('includeFinancials', e.target.checked)}
                        />
                      }
                      label="Include Financial Summary"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={reportSettings.includeMaintenace}
                          onChange={(e) => handleReportSettingChange('includeMaintenace', e.target.checked)}
                        />
                      }
                      label="Include Maintenance Reports"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={reportSettings.includeOccupancy}
                          onChange={(e) => handleReportSettingChange('includeOccupancy', e.target.checked)}
                        />
                      }
                      label="Include Occupancy Analytics"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={reportSettings.includeCompliance}
                          onChange={(e) => handleReportSettingChange('includeCompliance', e.target.checked)}
                        />
                      }
                      label="Include Compliance Updates"
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <ArgonBox display="flex" justifyContent="flex-end" mt={2}>
                  <ArgonButton
                    variant="gradient"
                    color="info"
                    onClick={handleSaveReportSettings}
                    startIcon={<Icon>save</Icon>}
                  >
                    Save Report Settings
                  </ArgonButton>
                </ArgonBox>
                {savedAlert && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Report settings saved successfully!
                  </Alert>
                )}
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            {/* Sabbath Settings Tab */}
            <ArgonBox>
              <Alert severity="info" sx={{ mb: 3 }}>
                <ArgonTypography variant="body2">
                  Configure Sabbath observance settings to automatically pause notifications and system activities during Sabbath hours.
                  The system will calculate sunset times based on your location.
                </ArgonTypography>
              </Alert>
              
              <SabbathSettings 
                user={{}} // Pass user data here when available
                onSettingsUpdate={handleSabbathUpdate}
                showStatus={true}
              />
            </ArgonBox>
          </TabPanel>
        </ArgonBox>
      </Card>
    </ArgonBox>
  );
}

export default ReportsAndSettings;